import { CompType, PropertyData, propertyHasCustomManualAdjustments } from '@aloft/toolkit-shared';
import {
  Block,
  Flex,
  Grid,
  Icon,
  ProductColor,
  Tooltip,
  Typography,
  theme,
} from '@aloft/toolkit-sui';

import { upperCase } from 'lodash';
import { PropsWithChildren } from 'react';
import { useFormContext } from 'react-hook-form';

import {
  selectEffectiveDate,
  selectOrder,
} from '../../../redux/features/selectors/orderSelectors.ts';
import { selectPropertyDataById } from '../../../redux/features/selectors/propertyDataSelectors/propertyDataSelectors.ts';
import { selectTransactionById } from '../../../redux/features/selectors/propertyDataSelectors/transactionSelectors.ts';
import { useAppSelector } from '../../../redux/hooks.ts';
import { CompPropertyDataState, SubjectPropertyDataState } from '../../../redux/types.ts';
import { getColorForStatusOrSubject } from '../../../utils/getColorForStatusOrSubject.ts';
import { CraftsmanPropertyHeader } from '../../CraftsmanPropertyHeader/CraftsmanPropertyHeader.tsx';
import { ValuationTablePropertyHeaderColorBand } from '../../CraftsmanPropertyHeader/internal/ValuationTablePropertyHeaderColorBand.tsx';
// eslint-disable-next-line aloft/prefer-sound-ui-button
import { ResetAdjustmentsButton } from '../ResetAdjustmentsButton.tsx';
import { PropertyGridForm, PropertyGridType } from '../types.ts';
import { getDataSourceDisplayInfo, getSimilarityScore } from '../utils.ts';

const SubHeaderItem = ({
  children,
  backgroundColor = 'surfaceAccent7',
}: PropsWithChildren<{ backgroundColor?: ProductColor }>) => (
  <Flex
    height="32px"
    alignItems="center"
    justifyContent="space-between"
    padding="4px 8px"
    backgroundColor={backgroundColor}
    __dangerDoNotUseSx={{ color: theme.colors.textOffset }}
  >
    {children}
  </Flex>
);

type HeaderPropertyGridSectionProps = {
  columnIndex: number;
  gridType: PropertyGridType;
};

const SubHeader = ({ gridType, columnIndex }: HeaderPropertyGridSectionProps) => {
  const effectiveDate = useAppSelector(selectEffectiveDate);
  const { hasArvOption } = useAppSelector(selectOrder);
  const subject = useAppSelector((state) => selectPropertyDataById(state, 'subject')) as
    | SubjectPropertyDataState
    | undefined;

  const { watch } = useFormContext<PropertyGridForm>();

  // TODO - AV-169
  const showAivComps = true;
  const showArvComps = true;

  const columnData = watch(`columns.${columnIndex}`);
  const manualAdjustments = watch(
    `columns.${columnIndex}.propertyData.editableData.manualAdjustments`,
  );

  const { propertyData, propertyId } = columnData || {};

  const isSubject = propertyId === 'subject';
  const isFeatureGrid = gridType === 'FeatureGrid';
  const hasCustomManualAdjustments = propertyHasCustomManualAdjustments(manualAdjustments);

  const propertyDataState = useAppSelector((state) => selectPropertyDataById(state, propertyId));
  const compType: CompType | undefined = !isSubject
    ? (propertyDataState as CompPropertyDataState).compData.compType
    : undefined;

  if (!subject || !effectiveDate || !propertyData) return null;

  const similarityScore = !isSubject
    ? getSimilarityScore(
        subject.subjectData,
        effectiveDate,
        propertyId,
        propertyData as PropertyData,
      )
    : undefined;

  if (isSubject) {
    return (
      <Grid
        width="100%"
        height="32px"
        gridTemplateColumns={isFeatureGrid && hasArvOption ? '1fr 1fr 1fr' : '1fr'}
        gap="1px"
        backgroundColor="strokeDefault"
        alignItems="center"
        __dangerDoNotUseSx={{ color: theme.colors.textOffset }}
      >
        <Typography
          variant="body2"
          __dangerDoNotUseSx={{
            alignContent: 'center',
            backgroundColor: theme.colors.surfaceAccent7,
            height: '100%',
            paddingLeft: '4px',
          }}
        >
          Feature
        </Typography>
        {isFeatureGrid && hasArvOption && (
          <>
            {showAivComps && (
              <Typography
                variant="body2"
                color="textPrimary"
                __dangerDoNotUseSx={{
                  alignContent: 'center',
                  backgroundColor: theme.colors.aiv,
                  height: '100%',
                  paddingLeft: '4px',
                }}
              >
                As-Is Value
              </Typography>
            )}
            {showArvComps && (
              <Typography
                variant="body2"
                __dangerDoNotUseSx={{
                  alignContent: 'center',
                  backgroundColor: theme.colors.arv,
                  height: '100%',
                  paddingLeft: '4px',
                }}
              >
                After-Repair Value
              </Typography>
            )}
          </>
        )}
      </Grid>
    );
  }
  if (isFeatureGrid) {
    if (!hasArvOption) {
      return (
        <SubHeaderItem>
          <span>Similarity Score</span>
          {similarityScore}
        </SubHeaderItem>
      );
    }

    return (
      <SubHeaderItem backgroundColor={`${compType!}Light`}>
        <Typography variant="body2" color="textPrimary">
          Similarity Score:&nbsp;
          {similarityScore ?? '-'}
        </Typography>
        <Flex
          backgroundColor={compType}
          __dangerDoNotUseSx={{
            padding: '2px 6px',
            borderRadius: '4px',
          }}
        >
          <Typography variant="body2" color="textPrimary">
            {upperCase(compType)}
          </Typography>
        </Flex>
      </SubHeaderItem>
    );
  }

  return (
    <Grid backgroundColor="strokeDefault" gap="1px" gridTemplateColumns="1fr 1fr">
      <SubHeaderItem>Feature</SubHeaderItem>
      <SubHeaderItem>
        Adjustment
        {hasCustomManualAdjustments && <ResetAdjustmentsButton columnIndex={columnIndex} />}
      </SubHeaderItem>
    </Grid>
  );
};

export const HeaderPropertyGridSection = ({
  columnIndex,
  gridType,
}: HeaderPropertyGridSectionProps) => {
  const { watch } = useFormContext<PropertyGridForm>();
  const columnData = watch(`columns.${columnIndex}`);

  const { propertyData, propertyId, type } = columnData || {};

  const transaction = useAppSelector((state) => selectTransactionById(state, propertyId));

  if (!propertyData || !transaction) return null;

  const isSubject = propertyId === 'subject';
  const propertyStatus = isSubject ? 'Subject' : (transaction.status ?? 'Off-Market');
  const color = getColorForStatusOrSubject(propertyStatus);

  const isFeatureGrid = gridType === 'FeatureGrid';

  if (type === 'property') {
    return (
      <Flex
        flexDirection="column"
        position="sticky"
        top="0"
        zIndex={theme.zIndices.sticky}
        __dangerDoNotUseSx={{ borderTop: `1px solid ${theme.colors.strokeDefault}` }}
      >
        <CraftsmanPropertyHeader
          propertyId={propertyId}
          showDataSourceDrawerToggler={isFeatureGrid}
        />
        <SubHeader gridType={gridType} columnIndex={columnIndex} />
      </Flex>
    );
  }

  const { icon, text, subText } = getDataSourceDisplayInfo(type, propertyData);

  return (
    <Flex
      flexDirection="column"
      position="sticky"
      top="0"
      zIndex={theme.zIndices.sticky}
      alignItems="center"
      __dangerDoNotUseSx={{
        borderTop: `1px solid ${theme.colors.strokeDefault}`,
        color: theme.colors.buttonDefault,
      }}
    >
      <Flex flexDirection="column" width="100%" height="110px" backgroundColor="surface1">
        <Flex flexDirection="column" alignItems="center" justifyContent="center" flexGrow={1}>
          <Icon size="xl" icon={icon} color="buttonDefault" />
          {text}
        </Flex>
        <ValuationTablePropertyHeaderColorBand color={color} />
      </Flex>
      <Block
        width="100%"
        height="32px"
        backgroundColor="surfaceAccent5"
        padding="4px 8px"
        textAlign="center"
        __dangerDoNotUseSx={{
          color: theme.colors.textPrimary,
          fontSize: theme.fontSizes.sm,
          fontWeight: 'bold',
        }}
      >
        <Tooltip label={subText} textAlign="center">
          <Typography truncate __dangerDoNotUseSx={{ whiteSpace: 'nowrap' }}>
            {subText}
          </Typography>
        </Tooltip>
      </Block>
    </Flex>
  );
};
